import Loading from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import {
  AdminOrgsQuery,
  Org_Plan_Type_Enum,
  useAdminDeleteOrgMutation,
  useAdminOrgsQuery,
  useAdminUpdateOrgPlanMutation,
} from '@gql'
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import debounce from 'lodash.debounce'
import { getOrgPath } from '@rolebase/shared/helpers/getOrgPath'
import { daysUntil } from '@rolebase/shared/model/orgPlan'
import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  ArchiveIcon,
  DeleteIcon,
  HideIcon,
  MemberIcon,
  MoreIcon,
  RestoreIcon,
  ShowIcon,
  SubscriptionIcon,
} from 'src/icons'
import AdminDangerConfirm from '../components/AdminDangerConfirm'
import AdminOrgOwnerModal from '../components/AdminOrgOwnerModal'
import AdminOrgPlanModal, {
  PLAN_LABEL_KEY,
} from '../components/AdminOrgPlanModal'

const PAGE_SIZE = 50

type AdminOrg = AdminOrgsQuery['org'][number]

// Le plan colore la ligne : on lit une grille tarifaire d'un coup d'œil.
const PLAN_COLOR: Record<Org_Plan_Type_Enum, string> = {
  [Org_Plan_Type_Enum.Free]: 'gray',
  [Org_Plan_Type_Enum.CartoSolo]: 'blue',
  [Org_Plan_Type_Enum.CartoGroov]: 'orange',
}

export default function AdminOrgsPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value)
        setOffset(0)
      }, 300),
    []
  )

  const { data, loading, error, refetch } = useAdminOrgsQuery({
    variables: {
      limit: PAGE_SIZE,
      offset,
      search: debouncedSearch ? `%${debouncedSearch}%` : '%',
    },
  })

  const totalCount = data?.org_aggregate.aggregate?.count ?? 0

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    debouncedSetSearch(e.target.value)
  }

  return (
    <>
      <Heading size="md" mb={6}>
        {t('SuperAdmin.orgs.heading')}
      </Heading>

      <Input
        placeholder={t('SuperAdmin.orgs.searchPlaceholder')}
        value={search}
        onChange={handleSearchChange}
        mb={4}
        maxW="400px"
      />

      {loading && <Loading active center />}
      {error && <TextError error={error} />}

      {data && (
        <>
          <Text mb={3} fontSize="sm" color="gray.500">
            {totalCount} {t('SuperAdmin.orgs.total')}
          </Text>

          {/*
            🔑 Carte + conteneur, comme `APICard` dans Réglages → API — le seul
            autre tableau de l'application (ADM09-D2). Le tableau était posé nu
            sur le fond de page, ce qui lui donnait un air étranger au reste et
            laissait ses 7 colonnes déborder sur le corps de page.
            `px={0}` reprend son réglage : ce sont les cellules qui portent la
            marge intérieure, pas la carte.
          */}
          <Card>
            <CardBody px={0}>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('SuperAdmin.orgs.columns.name')}</Th>
                      <Th>{t('SuperAdmin.orgs.columns.owner')}</Th>
                      <Th>{t('SuperAdmin.orgs.columns.plan')}</Th>
                      <Th>{t('SuperAdmin.orgs.columns.endsAt')}</Th>
                      <Th isNumeric>{t('SuperAdmin.orgs.columns.members')}</Th>
                      <Th>{t('SuperAdmin.orgs.columns.state')}</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.org.map((org) => (
                      <AdminOrgRow key={org.id} org={org} onUpdated={refetch} />
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>

          {totalCount > PAGE_SIZE && (
            <HStack mt={4} spacing={4} justify="center">
              <Button
                size="sm"
                isDisabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                {t('SuperAdmin.pagination.previous')}
              </Button>
              <Text fontSize="sm">
                {Math.floor(offset / PAGE_SIZE) + 1} /{' '}
                {Math.ceil(totalCount / PAGE_SIZE)}
              </Text>
              <Button
                size="sm"
                isDisabled={offset + PAGE_SIZE >= totalCount}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                {t('SuperAdmin.pagination.next')}
              </Button>
            </HStack>
          )}
        </>
      )}
    </>
  )
}

interface RowProps {
  org: AdminOrg
  onUpdated(): void
}

function AdminOrgRow({ org, onUpdated }: RowProps) {
  const { t } = useTranslation()
  const planModal = useDisclosure()
  const ownerModal = useDisclosure()
  const archiveConfirm = useDisclosure()
  const deleteConfirm = useDisclosure()

  const toast = useToast()
  const [updateOrgPlan] = useAdminUpdateOrgPlanMutation()
  const [deleteOrg] = useAdminDeleteOrgMutation()

  const isArchived = !!org.archivedAt
  const isDisabled = !!org.disabledAt

  /**
   * TOUS les propriétaires, triés par nom.
   *
   * 🔴 Une organisation peut en avoir plusieurs — mesuré le 15/08, aucune
   * contrainte ne l'interdit. N'en afficher qu'un (`members[0]`) donnait un nom
   * choisi au hasard par le serveur, faute de tri : « Cercle d'Ancrage UdN »
   * affichait Laurent Burget alors que Yohan est propriétaire lui aussi. Un
   * propriétaire caché est pire qu'une colonne un peu plus haute.
   *
   * ⚠️ Le tri est fait ici, pas dans la requête : `adminOrgs.gql` est compilé
   * dans les types générés, un `order_by` y coûterait un codegen pour rien.
   *
   * ⚠️ Une organisation peut aussi n'en avoir AUCUN d'actif : le sien a été
   * archivé. La ligne doit rester lisible plutôt que casser la page entière.
   */
  const owners = useMemo(
    () => [...org.members].sort((a, b) => a.name.localeCompare(b.name)),
    [org.members]
  )

  const remainingDays = org.planEndsAt ? daysUntil(org.planEndsAt) : undefined

  /**
   * 🔑 Toute action passe par ici, et l'échec DOIT se voir.
   *
   * Sur une console dont le métier est destructif, une promesse rejetée en
   * silence est le pire mode de défaillance : la ligne ne bouge pas, rien
   * n'explique pourquoi, et on recommence en croyant avoir mal cliqué. Un refus
   * de permission ou une contrainte de clé étrangère doivent s'afficher tels
   * quels — c'est ce message-là qui dit quoi faire ensuite.
   *
   * L'erreur est relancée à dessein : `AdminDangerConfirm` doit garder sa
   * fenêtre ouverte sur un échec, et le carnet d'incidents (ENR-09a) en reçoit
   * la trace. Le toast a déjà prévenu l'utilisateur.
   */
  const run = async (action: () => Promise<unknown>) => {
    try {
      await action()
      onUpdated()
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.message || undefined,
        status: 'error',
        isClosable: true,
      })
      throw error
    }
  }

  const handleArchive = () =>
    run(() =>
      updateOrgPlan({
        variables: {
          id: org.id,
          values: { archivedAt: new Date().toISOString() },
        },
      })
    )

  const handleRestore = () =>
    run(() =>
      updateOrgPlan({
        variables: { id: org.id, values: { archivedAt: null } },
      })
    )

  const handleToggleDisabled = () =>
    run(() =>
      updateOrgPlan({
        variables: {
          id: org.id,
          values: { disabledAt: isDisabled ? null : new Date().toISOString() },
        },
      })
    )

  const handleDelete = () => run(() => deleteOrg({ variables: { id: org.id } }))

  return (
    <>
      <Tr opacity={isArchived ? 0.55 : 1}>
        <Td>
          <Link to={`${getOrgPath(org)}/?admin`} target="_blank">
            <Text
              fontWeight="medium"
              color="blue.500"
              _hover={{ textDecoration: 'underline' }}
            >
              {org.name}
            </Text>
          </Link>
          <Text fontSize="xs" color="gray.500">
            {org.slug}
          </Text>
        </Td>

        {/*
          🔑 L'email passe au survol (ADM09-D5). Affiché sous le nom, il faisait
          deux lignes par propriétaire — donc quatre pour « Cercle d'Ancrage UdN
          (CA) », qui en a deux — et c'est cette hauteur, pas une police, qui
          donnait à la page son air désordonné.
          ⚠️ Le nom devient un lien `mailto:` plutôt qu'une simple infobulle :
          une adresse qu'on ne peut ni copier ni cliquer serait une perte sèche,
          alors que cette page sert justement à écrire aux gens. Il est bleu
          comme les autres liens du tableau — une affordance invisible n'en est
          pas une.
        */}
        <Td>
          {owners.length === 0 ? (
            <Text fontSize="sm" color="orange.500">
              {t('SuperAdmin.orgs.noOwner')}
            </Text>
          ) : (
            owners.map((owner) => {
              const email = owner.user?.email

              return (
                <Box key={owner.id} fontSize="sm" whiteSpace="nowrap">
                  {email ? (
                    <Tooltip label={email} openDelay={300} hasArrow>
                      <Text
                        as="a"
                        href={`mailto:${email}`}
                        color="blue.500"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        {owner.name}
                      </Text>
                    </Tooltip>
                  ) : (
                    <>
                      {owner.name}
                      <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                        {t('SuperAdmin.orgs.noAccount')}
                      </Text>
                    </>
                  )}
                </Box>
              )
            })
          )}
        </Td>

        <Td whiteSpace="nowrap">
          <Tag size="sm" colorScheme={PLAN_COLOR[org.plan]}>
            {t(PLAN_LABEL_KEY[org.plan])}
          </Tag>
        </Td>

        {/* nowrap : « Pas d'expiration » se coupait en deux (ADM09-D7). */}
        <Td whiteSpace="nowrap">
          {org.planEndsAt && remainingDays !== undefined ? (
            <>
              <Text fontSize="sm">
                {new Date(org.planEndsAt).toLocaleDateString()}
              </Text>
              <Text
                fontSize="xs"
                color={remainingDays < 0 ? 'red.500' : 'gray.500'}
              >
                {remainingDays < 0
                  ? t('SuperAdmin.orgs.expired')
                  : t('SuperAdmin.orgs.daysLeft', { count: remainingDays })}
              </Text>
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">
              {t('SuperAdmin.orgs.noExpiration')}
            </Text>
          )}
        </Td>

        <Td isNumeric>{org.members_aggregate.aggregate?.count ?? 0}</Td>

        <Td whiteSpace="nowrap">
          {isArchived ? (
            <Tag size="sm" colorScheme="gray">
              {t('SuperAdmin.orgs.states.archived')}
            </Tag>
          ) : isDisabled ? (
            <Tag size="sm" colorScheme="orange">
              {t('SuperAdmin.orgs.states.disabled')}
            </Tag>
          ) : (
            <Tag size="sm" colorScheme="green">
              {t('SuperAdmin.orgs.states.active')}
            </Tag>
          )}
        </Td>

        <Td>
          {/*
            🔴 `<Portal>` n'est pas décoratif : la ligne d'une organisation
            archivée porte `opacity={0.55}`, et l'opacité d'un parent s'applique
            à TOUS ses descendants — un enfant ne peut pas l'annuler. Le menu,
            rendu dans la ligne, héritait donc de sa transparence et devenait
            illisible précisément là où les actions comptent le plus
            (restaurer, supprimer). Le portail le sort de la ligne.
            Il le sort aussi du `TableContainer`, dont l'`overflow` pourrait le
            rogner. Motif repris d'`ActionsMenu`, l'usage maison.
          */}
          <Menu isLazy>
            <MenuButton
              as={IconButton}
              aria-label={t('SuperAdmin.orgs.actions')}
              icon={<MoreIcon size={18} />}
              size="sm"
              variant="ghost"
            />
            <Portal>
              <MenuList shadow="lg" zIndex={2000}>
                <MenuItem
                  icon={<SubscriptionIcon size={18} />}
                  onClick={planModal.onOpen}
                >
                  {t('SuperAdmin.orgs.menu.changePlan')}
                </MenuItem>
                <MenuItem
                  icon={<MemberIcon size={18} />}
                  onClick={ownerModal.onOpen}
                >
                  {t('SuperAdmin.orgs.menu.transferOwner')}
                </MenuItem>

                <MenuDivider />

                {!isArchived && (
                  <MenuItem
                    icon={
                      isDisabled ? (
                        <ShowIcon size={18} />
                      ) : (
                        <HideIcon size={18} />
                      )
                    }
                    onClick={handleToggleDisabled}
                  >
                    {isDisabled
                      ? t('SuperAdmin.orgs.menu.enable')
                      : t('SuperAdmin.orgs.menu.disable')}
                  </MenuItem>
                )}

                {isArchived ? (
                  <>
                    <MenuItem
                      icon={<RestoreIcon size={18} />}
                      onClick={handleRestore}
                    >
                      {t('SuperAdmin.orgs.menu.restore')}
                    </MenuItem>
                    <MenuItem
                      icon={<DeleteIcon size={18} />}
                      color="red.500"
                      onClick={deleteConfirm.onOpen}
                    >
                      {t('SuperAdmin.orgs.menu.delete')}
                    </MenuItem>
                  </>
                ) : (
                  <MenuItem
                    icon={<ArchiveIcon size={18} />}
                    onClick={archiveConfirm.onOpen}
                  >
                    {t('SuperAdmin.orgs.menu.archive')}
                  </MenuItem>
                )}
              </MenuList>
            </Portal>
          </Menu>
        </Td>
      </Tr>

      {planModal.isOpen && (
        <AdminOrgPlanModal
          org={org}
          isOpen
          onClose={planModal.onClose}
          onUpdated={onUpdated}
        />
      )}

      {ownerModal.isOpen && (
        <AdminOrgOwnerModal
          org={org}
          isOpen
          onClose={ownerModal.onClose}
          onUpdated={onUpdated}
        />
      )}

      {archiveConfirm.isOpen && (
        <AdminDangerConfirm
          isOpen
          onClose={archiveConfirm.onClose}
          expectedName={org.name}
          title={t('SuperAdmin.orgs.danger.archiveTitle')}
          description={
            <Text>
              <Trans
                i18nKey="SuperAdmin.orgs.danger.archiveInfo"
                values={{ name: org.name }}
                components={{ b: <strong /> }}
              />
            </Text>
          }
          confirmLabel={t('SuperAdmin.orgs.menu.archive')}
          onConfirm={handleArchive}
        />
      )}

      {deleteConfirm.isOpen && (
        <AdminDangerConfirm
          isOpen
          onClose={deleteConfirm.onClose}
          expectedName={org.name}
          title={t('SuperAdmin.orgs.danger.deleteTitle')}
          description={
            <>
              <Text>
                <Trans
                  i18nKey="SuperAdmin.orgs.danger.deleteInfo"
                  values={{ name: org.name }}
                  components={{ b: <strong /> }}
                />
              </Text>
              <Text mt={3} color="red.500">
                {t('SuperAdmin.orgs.danger.deleteIrreversible')}
              </Text>
            </>
          }
          confirmLabel={t('SuperAdmin.orgs.menu.delete')}
          onConfirm={handleDelete}
        />
      )}
    </>
  )
}
